import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CheckoutStatus, State } from "../state";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { SubscriptionPeriod } from "@/types/types";

export const shopSlice = createSlice({
    name: "shop",
    initialState: {
        period: "yearly", // INTERVAL 📅 Force yearly subscriptions only - change back to "monthly" to re-enable
        products: [],
        selectedProducts: [],
        checkoutStatus: "uninitialized",
        isRestoring: false,
        hasUnsavedChanges: false,
    } as State.Shop,
    reducers: {
        setSubscriptionPeriod: (state, action: PayloadAction<SubscriptionPeriod>) => {
            // INTERVAL 📅 Force yearly period, ignore incoming changes - restore original line below to re-enable monthly
            state.period = "yearly";
            // state.period = action.payload;
        },

        setProducts: (state, action: PayloadAction<State.Product[]>) => {
            state.products = action.payload;
        },

        updateSelectedProduct: (state, action: PayloadAction<{ priceId: string; quantity: number }>) => {
            const { priceId, quantity } = action.payload;

            // Helper: find the full Price object for a given priceId
            const findPriceById = (id: string) => {
                for (const product of state.products) {
                    const match = product.prices.find((p) => p && p.priceId === id);
                    if (match) return match;
                }
                return null;
            };

            const incomingPrice = findPriceById(priceId);

            // Determine if the incoming price represents a seat purchase (recurring) or a one-time purchase
            const isSeatPrice = (price: any | null) => price && price.seatType !== null && price.seatType !== undefined;

            // Check if a price is the onboarding fee
            const isOnboardingFee = (price: any | null) => {
                if (!price) return false;
                // Find the product that contains this price and check if it's the onboarding fee
                for (const product of state.products) {
                    const priceMatch = product.prices.find((p) => p && p.priceId === price.priceId);
                    if (priceMatch && product.name.includes("Onboarding")) {
                        return true;
                    }
                }
                return false;
            };

            const incomingIsSeat = isSeatPrice(incomingPrice);
            const incomingIsOnboarding = isOnboardingFee(incomingPrice);

            // Determine the category (seat vs one-time) of the **current** cart, if it isn't empty
            let currentCartIsSeat: boolean | null = null;
            let currentCartHasOnboarding = false;
            if (state.selectedProducts.length > 0) {
                const firstPrice = findPriceById(state.selectedProducts[0].priceId);
                currentCartIsSeat = isSeatPrice(firstPrice);
                
                // Check if current cart contains onboarding fee
                currentCartHasOnboarding = state.selectedProducts.some(p => {
                    const price = findPriceById(p.priceId);
                    return isOnboardingFee(price);
                });
            }

            // ❌ Mixing seat purchases with one-time purchases is not allowed.
            // EXCEPTION: Onboarding fees can be mixed with seat products for onboarding flow
            // If categories differ, reset the current selection before applying the new update.
            const shouldClearCart = currentCartIsSeat !== null && 
                                  currentCartIsSeat !== incomingIsSeat &&
                                  !incomingIsOnboarding && 
                                  !currentCartHasOnboarding;
            
            if (shouldClearCart) {
                state.selectedProducts = [];
            }

            // If quantity is 0, remove the product (after potential reset)
            if (quantity === 0) {
                state.selectedProducts = state.selectedProducts.filter((product) => product.priceId !== priceId);
                return;
            }

            // Check if product already exists in the (possibly reset) selection
            const existingProductIndex = state.selectedProducts.findIndex((product) => product.priceId === priceId);

            if (existingProductIndex !== -1) {
                // Update existing product
                state.selectedProducts[existingProductIndex].quantity = quantity;
            } else {
                // Add new product
                state.selectedProducts.push({ priceId, quantity });
            }
        },

        clearSelectedProducts: (state) => {
            state.selectedProducts = [];
        },

        setCheckoutStatus: (state, action: PayloadAction<CheckoutStatus>) => {
            state.checkoutStatus = action.payload;
        },

        setSelectedProducts: (state, action: PayloadAction<{ priceId: string; quantity: number }[]>) => {
            // Filter out any unknown priceIds that are not present in the current catalog
            const knownPriceIds = new Set<string>();
            for (const product of state.products) {
                for (const price of product.prices) {
                    if (price && price.priceId) knownPriceIds.add(price.priceId);
                }
            }

            const filtered = action.payload.filter(item => knownPriceIds.has(item.priceId));
            state.selectedProducts = filtered;
            state.hasUnsavedChanges = false; // Restored state is considered saved
        },

        setRestoring: (state, action: PayloadAction<boolean>) => {
            state.isRestoring = action.payload;
        },

        markUnsavedChanges: (state) => {
            state.hasUnsavedChanges = true;
        },

        clearUnsavedChanges: (state) => {
            state.hasUnsavedChanges = false;
        },
    },
});

export const { 
    setSubscriptionPeriod, 
    setProducts, 
    updateSelectedProduct, 
    clearSelectedProducts, 
    setCheckoutStatus,
    setSelectedProducts,
    setRestoring,
    markUnsavedChanges,
    clearUnsavedChanges
} = shopSlice.actions;

export const useShopSlice = () => useSelector((state: RootState) => state.shop);

export default shopSlice.reducer;
